/**
 * Scoring Bridge — Puente scoring v1 → v2 para OrchestrationResult
 *
 * Construye un ScoreResult completo (D1+D2+D3+flags) a partir
 * de los campos existentes en OrchestrationResult, sin romper
 * la interfaz actual.
 *
 * @module services/scoring/scoringBridge
 * @version 1.0.0
 */

import type { OrchestrationResult } from '../geocoding/orchestrator/types';
import type { ScoreResult as _ScoreResult } from '../../types/scoring';
import { buildD1Input, computeD1Origen } from './d1Origen';
import { buildD2Input, computeD2Precision } from './d2Precision';
import { buildD3Input, computeD3Validacion } from './d3Validacion';
import type { SupramunicipalContext } from './d3Validacion';
import { computeFinalScore } from './finalScore';

/**
 * Enriquece un OrchestrationResult con scoring v2.
 *
 * Extrae los datos necesarios de los campos existentes del resultado
 * (geocoderUsed, crossValidationScore, discrepancyMeters, spatialValidation,
 * sourcesConsulted) y ejecuta el pipeline D1→D2→D3→Final.
 *
 * @param result - OrchestrationResult con campos v1
 * @param supra - Contexto supramunicipal opcional (B1b). Cuando el registro quedó
 *   fuera del polígono municipal (`outOfBounds`) y la infra es legítimamente
 *   supramunicipal, activa el override de banda en D3 y el flag en finalScore.
 * @returns OrchestrationResult con scoreDetail añadido
 */
export function enrichWithScoringV2(
  result: OrchestrationResult,
  supra?: SupramunicipalContext & { outOfBounds: boolean },
): OrchestrationResult {
  // Sin resultado de geocodificación → scoring mínimo
  if (!result.geocoding) {
    return result; // scoreDetail queda undefined — sin coords no hay score
  }

  const geocoderUsed = result.geocoderUsed || 'none';

  // ── D1 Origen ──
  const d1Input = buildD1Input(
    geocoderUsed,
    result.geocoding.source,    // sourceHint del resultado
    undefined,                   // isOffline: inferido por buildD1Input
  );
  const d1 = computeD1Origen(d1Input);

  // ── D2 Precisión ──
  // Inferir si coords proceden del documento (geocoderUsed = 'none' o vacío)
  const fromDocument = geocoderUsed === 'none' || geocoderUsed === '';
  const d2Input = buildD2Input(
    result.geocoding.matchType,  // matchType del geocodificador
    result.geocoding.fuzzyScore, // fuzzyScore 0-1 (ya normalizado)
    fromDocument,
  );
  const d2 = computeD2Precision(d2Input);

  // ── D3 Validación ──
  const spatialValid = result.spatialValidation?.isValid;
  const sourcesCount = result.sourcesConsulted?.length ?? 0;
  const primaryConfidence = result.geocoding?.confidence;
  const d3Input = buildD3Input(
    spatialValid,
    result.crossValidationScore,
    sourcesCount,
    result.discrepancyMeters ?? undefined,
    primaryConfidence,
  );
  // B1b: contexto supramunicipal opcional. computeD3Validacion solo aplica el
  // override de banda cuando spatialValid === false (gate interno); reenviar el
  // ctx siempre que llegue es seguro.
  const supraCtx: SupramunicipalContext | undefined = supra
    ? { x: supra.x, y: supra.y, codigoINE: supra.codigoINE, infrastructureType: supra.infrastructureType }
    : undefined;
  const d3 = computeD3Validacion(d3Input, supraCtx);

  // ── Score Final ──
  const scoreDetail = computeFinalScore({
    d1,
    d2,
    d3,
    hasOriginalCoords: fromDocument,
    isGeocoded: !fromDocument,
    // B1b: supramunicipal — finalScore hace gate con
    // outOfBounds && x && y && codigoINE && infrastructureType.
    outOfBounds: supra?.outOfBounds,
    x: supra?.x,
    y: supra?.y,
    codigoINE: supra?.codigoINE,
    infrastructureType: supra?.infrastructureType,
  });

  return { ...result, scoreDetail };
}
